import { InputAdornment, TableRow } from "@mui/material";
import { ReactComponent as RemoveFilled } from "../../../../../../common/icons/delete.svg";
import { DatePicker } from "@mui/x-date-pickers";
import { StyledTableCell } from "../../../../../../common/components/functional/simpleTable/styled";
import { useEffect, useState } from "react";
import { GarnishmentRestriction } from "../../paymentProhibitionAPI";
import { format, isValid } from "date-fns";
import { TextFieldLight } from "../../../../../../common/components/styled/styled";

interface Props {
  item: GarnishmentRestriction;
  disableExceptions: boolean;
  handleExceptionChange: (
    value: string | null,
    exceptionId: string,
    type: string,
    field: string,
  ) => void;
  deleteException: (exceptionId: string, type: string) => void;
}

const GarnishmentRestrictionItem = ({
  item,
  disableExceptions,
  handleExceptionChange,
  deleteException,
}: Props) => {
  const [startDate, setStartDate] = useState<Date | null>(
    item?.start ? new Date(item.start) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    item?.end ? new Date(item.end) : null,
  );
  const [amount, setAmount] = useState<string>(item?.amount || "");

  useEffect(() => {
    setStartDate(item?.start ? new Date(item.start) : null);
    setEndDate(item?.end ? new Date(item.end) : null);
    setAmount(item?.amount || "");
  }, [item]);

  const handleDateChange = (date: any, field: string) => {
    let newDate = date;

    // Check if the date is not null and is valid
    if (date && isValid(date)) {
      // If the field is "end", set the date to the last day of the month
      if (field === "end") {
        newDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      } else {
        // If the field is "start", set the date to the first day of the month
        newDate = new Date(date.getFullYear(), date.getMonth(), 1);
      }
    } else {
      // If the date is null or invalid, set newDate to null
      newDate = null;
    }

    // Update local state with the new date or null
    field === "start" ? setStartDate(newDate) : setEndDate(newDate);

    // Check if the new date (which might be null) should update the item in paymentProhibitions
    if ((newDate === null || isValid(newDate)) && item?.id) {
      const formattedDate = newDate ? format(newDate, "yyyy-MM-dd") : null;
      handleExceptionChange(
        formattedDate,
        item.id,
        "garnishmentRestriction",
        field,
      );
    }
  };

  const handleInputChange = (value: string) => {
    if (item?.id) {
      // Update local state
      setAmount(value);
      // Update the item in paymentProhibitions with the new amount
      handleExceptionChange(value, item.id, "garnishmentRestriction", "amount");
    }
  };

  return (
    <TableRow>
      <StyledTableCell sx={{ paddingLeft: "15px" }}>
        <DatePicker
          views={["year", "month"]}
          openTo="month"
          disabled={disableExceptions}
          format="MM/yyyy"
          value={startDate || null}
          onChange={(date) => handleDateChange(date, "start")}
          slotProps={{
            textField: {
              size: "small",
              label: null,
              error: (startDate && endDate && startDate > endDate) || false,
              sx: {
                width: "135px",
                paddingTop: "0px",
                border: "none",
              },
            },
          }}
        />
      </StyledTableCell>
      <StyledTableCell>{"-"}</StyledTableCell>
      <StyledTableCell>
        <DatePicker
          views={["year", "month"]}
          openTo="month"
          disabled={disableExceptions}
          format="MM/yyyy"
          value={endDate || null}
          onChange={(date) => handleDateChange(date, "end")}
          slotProps={{
            textField: {
              size: "small",
              label: null,
              error: (startDate && endDate && startDate > endDate) || false,
              sx: {
                width: "135px",
                paddingTop: "0px",
                border: "none",
              },
            },
          }}
        />
      </StyledTableCell>
      <StyledTableCell>
        <TextFieldLight
          disabled={disableExceptions}
          error={
            !item.amount ||
            parseFloat(item.amount.replace(",", ".")) <= 0 ||
            !/^\d*([,.]?\d+)?$/.test(item.amount)
          }
          InputProps={{
            style: { fontSize: "14px", padding: "0px" },
            endAdornment: (
              <InputAdornment sx={{ paddingRight: "10px" }} position="end">
                {"€"}
              </InputAdornment>
            ),
          }}
          size="small"
          value={amount || ""}
          onChange={(e) => handleInputChange(e.target.value)}
        />
      </StyledTableCell>
      <StyledTableCell>
        <RemoveFilled
          onClick={() =>
            !disableExceptions &&
            item?.id &&
            deleteException(item.id, "garnishmentRestriction")
          }
          style={{
            width: "16px",
            height: "16px",
            cursor: !disableExceptions ? "pointer" : "default",
            margin: "5px 10px 0px 5px",
            opacity: !disableExceptions ? 1 : 0.5,
          }}
        />
      </StyledTableCell>
    </TableRow>
  );
};
export default GarnishmentRestrictionItem;
